import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { History, Settings as SettingsIcon, Clapperboard, LogOut, User, Users, UserRound, Coins, ShoppingBag, LayoutDashboard, Boxes, Bell, MessageSquare, Receipt, LifeBuoy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getCreditBalance } from "@/lib/api";
import { useHeygenCreation } from "@/lib/useHeygenCreation";
import { Button } from "@/components/ui/button";

const links = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/video-jualan", label: "Video Jualan", icon: ShoppingBag, testid: "nav-sales-video" },
  { to: "/shortdrama", label: "ShortDrama Studio", icon: Clapperboard, testid: "nav-shortdrama" },
  { to: "/model-hub", label: "Model Hub", icon: Boxes, testid: "nav-model-hub" },
  { to: "/avatar-saya", label: "Avatar Saya", icon: UserRound, testid: "nav-avatar" },
  { to: "/riwayat", label: "Riwayat Video", icon: History, testid: "nav-history" },
  { to: "/kredit", label: "Kredit", icon: Coins, testid: "nav-credits" },
  { to: "/transaksi", label: "Transaksi Saya", icon: Receipt, testid: "nav-transactions" },
  { to: "/affiliate", label: "Affiliate", icon: Users, testid: "nav-affiliate" },
  { to: "/chat", label: "Chat AI", icon: MessageSquare, testid: "nav-chat" },
  { to: "/notifikasi", label: "Notifikasi", icon: Bell, testid: "nav-notifications" },
  { to: "/bantuan", label: "Bantuan", icon: LifeBuoy, testid: "nav-help" },
  { to: "/pengaturan", label: "Pengaturan", icon: SettingsIcon, testid: "nav-settings" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [credit, setCredit] = useState(null);
  const heygen = useHeygenCreation();
  // Avatar creation is HeyGen-only; do not offer it as an active feature while disabled.
  const visibleLinks = links.filter((link) => link.to !== "/avatar-saya" || heygen.enabled);

  useEffect(() => {
    let cancelled = false;
    let latestRequest = 0;
    setCredit(null);
    const refresh = () => {
      const request = ++latestRequest;
      return getCreditBalance().then((d) => {
        if (!cancelled && request === latestRequest) setCredit(d.credit_balance);
      }).catch(() => {});
    };
    refresh();
    window.addEventListener("credits-updated", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("credits-updated", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [user?.id]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col border-r border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="flex items-center gap-3 px-6 py-6 border-b border-slate-200">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
            <Clapperboard className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading font-bold text-slate-900 leading-tight tracking-tight">Mas Tofa AI</p>
            <p className="text-xs text-slate-500">AI Video Generator</p>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {visibleLinks.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-blue-50 text-blue-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4.5 w-4.5" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-slate-200 px-4 py-4">
          <div className="mb-2 flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm" data-testid="sidebar-credit">
            <Coins className="h-4 w-4 text-blue-600" />
            <span className="text-slate-600">Kredit:</span>
            <span className="ml-auto font-semibold text-blue-700" data-testid="sidebar-credit-value">{credit === null ? "—" : credit}</span>
          </div>
          <div className="flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700" data-testid="sidebar-user">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-600">
              <User className="h-4 w-4" />
            </span>
            <span className="truncate">Hi, {user?.name || "Pengguna"}</span>
          </div>
          <Button
            variant="ghost"
            data-testid="logout-btn"
            onClick={logout}
            className="mt-1 w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
          >
            <LogOut className="h-4 w-4 mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex flex-col">
        <header className="md:hidden sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/80 backdrop-blur-xl px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600 text-white">
              <Clapperboard className="h-4 w-4" />
            </div>
            <span className="font-heading font-bold text-slate-900">Mas Tofa AI</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700" data-testid="topbar-credit">
              <Coins className="h-3.5 w-3.5" /> {credit === null ? "—" : credit}
            </span>
            <span className="text-xs text-slate-600" data-testid="topbar-user">Hi, {user?.name || "Pengguna"}</span>
            <Button
              variant="ghost"
              size="sm"
              data-testid="logout-btn-mobile"
              aria-label="Keluar"
              title="Keluar"
              onClick={logout}
              className="text-slate-600 hover:text-red-600 hover:bg-red-50 px-2"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <nav className="md:hidden sticky top-[57px] z-10 flex overflow-x-auto border-b border-slate-200 bg-white">
          {visibleLinks.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap px-3 py-3 text-xs font-medium transition-colors duration-200 ${
                  isActive ? "text-blue-700 border-b-2 border-blue-600" : "text-slate-500"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1">{children}</main>
      </div>
    </div>
  );
};
